// src/components/DashboardLoader.jsx
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import HomePage from "./HomePage";
import DraftPage from "./DraftPage";
import WinRateTierSelection from "./WinRateTierSelection";
import TierData from "./tier/TierData";
import MetaLab from "./MetaLab";
import TeamPerformancePage from "../pages/TeamPerformancePage";
import DraftRecapPage from "../pages/DraftRecapPage";
import TacticBoardPage from "../pages/TacticBoardPage";
import LoginPage from "./LoginPage";
import heroList from "../data/heroList.json";
import { getHeroImageUrl } from "../utils/heroCatalog";
import {
  parseGamesFromExcel,
  parseComboStatsFromExcel,
  parseScrimStatsFromExcel,
} from "../utils/excelParser.js";
import { parseTeamPerformanceFromFile } from "../utils/teamPerformancePipeline";
import { buildComboStatsFromGames, mergeComboStats } from "../utils/comboStatsFromGames";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import { Cpu, Upload } from "lucide-react"; // ใช้ Icon เข้ามาช่วย
import BrandLogoBadge from "./ui/BrandLogoBadge";

const AUTH_STORAGE_KEY = "fsrov_auth";

/** * ====================================================================
 * PART 1: Visual Components (ยืมมาจาก MetaLab เพื่อความคุมธีม)
 * ====================================================================
 */

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Kanit:wght@300;400;500;600;700&family=Orbitron:wght@400;700;900&family=Rajdhani:wght@400;500;600;700&display=swap');
    body { background-color: #fff5f7; color: #111827; margin: 0; }
    @keyframes scan { 0% { top: -10%; opacity: 0; } 50% { opacity: 1; } 100% { top: 110%; opacity: 0; } }
    @keyframes pulse-glow { 0%, 100% { box-shadow: 0 0 20px rgba(255,16,38,0.2); } 50% { box-shadow: 0 0 40px rgba(255,16,38,0.6); } }
  `}</style>
);

const Glow = ({ c, s = 400, x, y }) => (
  <div
    style={{
      position: "absolute",
      width: s,
      height: s,
      left: x,
      top: y,
      borderRadius: "50%",
      filter: "blur(100px)",
      background: `radial-gradient(circle, ${c}80 0%, ${c}40 30%, transparent 70%)`,
      opacity: 0.6,
      pointerEvents: "none",
      zIndex: 0,
    }}
  />
);

const Grid = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      backgroundImage:
        "linear-gradient(rgba(255,16,38,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,16,38,0.05) 1px, transparent 1px)",
      backgroundSize: "60px 60px",
      opacity: 0.3,
      pointerEvents: "none",
      zIndex: 0,
    }}
  />
);

const HexPattern = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0 L45 15 L45 45 L30 60 L15 45 L15 15 Z' fill='none' stroke='%23ff1026' stroke-width='1'/%3E%3C/svg%3E")`,
      backgroundSize: "60px 60px",
      opacity: 0.05,
      pointerEvents: "none",
      zIndex: 0,
    }}
  />
);

const ScanLine = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
      borderRadius: "16px",
      zIndex: 10,
    }}
  >
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        height: "2px",
        background: "linear-gradient(90deg, transparent, #ff1026, transparent)",
        animation: "scan 3s linear infinite",
        boxShadow: "0 0 10px #ff1026",
      }}
    />
  </div>
);

const CornerAccents = ({ color = "#ff1026" }) => (
  <>
    <div style={{ position: "absolute", top: -2, left: -2, width: 20, height: 20, borderTop: `2px solid ${color}`, borderLeft: `2px solid ${color}`, pointerEvents: "none" }} />
    <div style={{ position: "absolute", top: -2, right: -2, width: 20, height: 20, borderTop: `2px solid ${color}`, borderRight: `2px solid ${color}`, pointerEvents: "none" }} />
    <div style={{ position: "absolute", bottom: -2, left: -2, width: 20, height: 20, borderBottom: `2px solid ${color}`, borderLeft: `2px solid ${color}`, pointerEvents: "none" }} />
    <div style={{ position: "absolute", bottom: -2, right: -2, width: 20, height: 20, borderBottom: `2px solid ${color}`, borderRight: `2px solid ${color}`, pointerEvents: "none" }} />
  </>
);

function DraftEntryGate({ children }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let timer = null;
    let raf = null;

    raf = requestAnimationFrame(() => {
      timer = setTimeout(() => {
        setReady(true);
      }, 450);
    });

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (ready) return children;

  return (
    <div
      style={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "'Rajdhani', sans-serif",
        background: "linear-gradient(180deg, #ffffff 0%, #fff2f4 100%)",
      }}
    >
      <div style={{ position: "absolute", inset: 0 }}>
        <Glow c="#ff1026" s={820} x="-20%" y="-5%" />
        <Glow c="#ff4d5f" s={620} x="78%" y="74%" />
        <Grid />
        <HexPattern />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35 }}
        style={{
          width: "100%",
          maxWidth: 460,
          position: "relative",
          zIndex: 1,
          textAlign: "center",
          background: "rgba(255,255,255,0.92)",
          border: "1px solid rgba(255,16,38,0.28)",
          borderRadius: 16,
          padding: "30px 22px",
          boxShadow: "0 14px 40px rgba(255,16,38,0.12)",
          color: "#111827",
        }}
      >
        <CornerAccents />
        <div style={{ marginBottom: 10, fontFamily: "'Orbitron', sans-serif", fontWeight: 800, letterSpacing: "0.08em", color: "#b91c1c" }}>
          PREPARING DRAFT
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>กำลังโหลดข้อมูลก่อนเลือก BO</div>
        <div style={{ marginTop: 8, color: "#1f2937", fontWeight: 600 }}>กรุณารอสักครู่...</div>

        <div
          style={{
            marginTop: 18,
            height: 8,
            width: "100%",
            borderRadius: 999,
            overflow: "hidden",
            background: "rgba(255,16,38,0.14)",
            border: "1px solid rgba(255,16,38,0.3)",
          }}
        >
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: "100%" }}
            transition={{ duration: 0.85, repeat: Infinity, ease: "linear" }}
            style={{
              height: "100%",
              width: "38%",
              background: "linear-gradient(90deg, transparent, #ff1026, transparent)",
            }}
          />
        </div>
      </motion.div>
    </div>
  );
}

/** Hook: ขนาดหน้าต่าง */
function useWindowSize() {
  const getSize = () => ({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  });
  const [size, setSize] = useState(getSize);
  useEffect(() => {
    const onResize = () => setSize(getSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
}

/** * ====================================================================
 * PART 2: Main Logic
 * ====================================================================
 */

export default function DashboardLoader() {
  // ----- ข้อมูลหลัก -----
  const [games, setGames] = useState([]);
  const [homeGames, setHomeGames] = useState([]);
  const [sheetComboStats, setSheetComboStats] = useState(null);
  const [dropdownDates, setDropdownDates] = useState([]);

  // ----- เก็บไฟล์/เวิร์กบุ๊ก -----
  const [excelFile, setExcelFile] = useState(null);
  const [excelWb, setExcelWb] = useState(null);
  const [teamPerformance, setTeamPerformance] = useState(null);

  // ----- UI -----
  const [uploaded, setUploaded] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);

  // ชีท Combo_Stats เก็บคอมโบไม่ครบ (ไฟล์ปัจจุบัน: 41 คู่ จาก 216 คู่ที่เล่นจริง และ
  // ไม่มี 3 ตัวเลย) เลยนับจาก log เป็นหลัก แล้วใช้ชีทเป็นตัวสำรอง — ทุกหน้าจะได้ชุดเดียวกัน
  const comboStats = useMemo(
    () => mergeComboStats(buildComboStatsFromGames(games), sheetComboStats),
    [games, sheetComboStats]
  );

  const navigate = useNavigate();
  const { width, height } = useWindowSize();
  const isMobile = width <= 768;
  const isAuthenticated = localStorage.getItem(AUTH_STORAGE_KEY) === "true";

  const parseWorkbookFile = useCallback(async (file, options = {}) => {
    const { autoOpenDashboard = true } = options;
    if (!file) return;

    setUploaded(false);
    setShowDashboard(false);
    setExcelFile(file);
    parseGamesFromExcel(file, (g) => {
      setGames(g);
      setIsProcessingUpload(false);
      setUploaded(true);
      setIsProcessingUpload(false);
      if (autoOpenDashboard) {
        setTimeout(() => {
          setShowDashboard(true);
          navigate("/dashboard");
        }, 1500);
      }
    });
    // Home overview reads All_Scrim (same sheet as Team Performance) so the two
    // headline win-rate figures agree, instead of the recent APL_Scrim block.
    parseScrimStatsFromExcel(file, setHomeGames, { preferAllScrim: true });

    parseComboStatsFromExcel(file, setSheetComboStats, (dropdowns) => {
      if (dropdowns?.dates) setDropdownDates(dropdowns.dates);
    });

    try {
      const perf = await parseTeamPerformanceFromFile(file, {
        includeOther: false,
        minGamesForDelta: 3,
      });
      setTeamPerformance(perf);
    } catch (err) {
      console.error("Team performance parse failed:", err);
      setTeamPerformance(null);
    }

    const buf = await file.arrayBuffer();
    const wb = XLSX.read(new Uint8Array(buf), { type: "array" });
    setExcelWb(wb);
  }, [navigate]);

  // ---------- Upload & Parse ----------
  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsProcessingUpload(true);
    try {
      await parseWorkbookFile(file, { autoOpenDashboard: true });
    } catch (err) {
      console.error("Failed to parse workbook:", err);
      setIsProcessingUpload(false);
    } finally {
      if (e?.target) e.target.value = "";
    }
  }

  // ---------- Gate Effect (เปลี่ยนเป็น System Boot Style) ----------
  function GateEffect({ processing = false }) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(255, 255, 255, 0.92)",
          backdropFilter: "blur(20px)",
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Orbitron', sans-serif",
        }}
      >
        <div style={{ position: "absolute", inset: 0 }}>
          <Grid />
          <Glow c="#ff1026" s={800} x="50%" y="50%" />
        </div>

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={{ zIndex: 10, textAlign: "center" }}
        >
          <div style={{
            display: 'inline-flex',
            padding: '20px',
            border: '2px solid #ff1026',
            borderRadius: '50%',
            marginBottom: '20px',
            boxShadow: '0 0 50px rgba(255,16,38,0.5)'
          }}>
            <Cpu size={64} color="#ff1026" />
          </div>

          <h1 style={{
            color: '#b91c1c',
            fontSize: '2.5rem',
            letterSpacing: '0.2em',
            textShadow: '0 0 20px #ff1026'
          }}>
            {processing ? "ANALYZING FILE" : "SYSTEM INITIALIZED"}
          </h1>

          <motion.div
            style={{
              height: '4px',
              width: '300px',
              background: '#333',
              margin: '20px auto',
              borderRadius: '2px',
              overflow: 'hidden'
            }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              style={{
                height: '100%',
                background: '#ff1026',
                boxShadow: '0 0 10px #ff1026'
              }}
            />
          </motion.div>

          <p style={{ color: '#b91c1c', fontFamily: "'Rajdhani', sans-serif", letterSpacing: '0.1em' }}>
            {processing ? "PROCESSING EXCEL DATA..." : "LOADING GAME DATA MODULES..."}
          </p>
        </motion.div>
      </motion.div>
    );
  }

  // ---------- หน้า Upload (Theme MetaLab) ----------
  function UploadPage() {
    return (
      <>
        <GlobalStyles />
        <div
          style={{
            minHeight: height,
            width: "100vw",
            background: "linear-gradient(180deg, #ffffff 0%, #fff1f3 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            fontFamily: "'Rajdhani', sans-serif",
          }}
        >
          {/* Background Elements */}
          <div style={{ position: "fixed", inset: 0 }}>
            <Glow c="#ff1026" s={800} x="-20%" y="-10%" />
            <Glow c="#ff4d5f" s={600} x="80%" y="70%" />
            <Grid />
            <HexPattern />
          </div>

          <AnimatePresence>
            {!showDashboard && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 1.1, filter: "blur(10px)" }}
                transition={{ duration: 0.5 }}
                style={{
                  position: "relative",
                  zIndex: 10,
                  width: "100%",
                  maxWidth: "500px",
                  padding: "20px",
                }}
              >
                {/* Main Card */}
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255,16,38,0.3)",
                    borderRadius: "16px",
                    padding: isMobile ? "30px 20px" : "40px",
                    textAlign: "center",
                    position: "relative",
                    boxShadow: "0 12px 40px rgba(255,16,38,0.18)",
                  }}
                >
                  <ScanLine />
                  <CornerAccents />

                  {/* Header Icon */}
                  <div style={{ marginBottom: "20px" }}>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "80px",
                        height: "80px",
                        borderRadius: "20px",
                        background: "linear-gradient(135deg, rgba(255,16,38,0.2), rgba(255,255,255,0))",
                        border: "1px solid rgba(255,16,38,0.5)",
                        boxShadow: "0 0 20px rgba(255,16,38,0.2)",
                        animation: "float 3s ease-in-out infinite", // Reuse float keyframe if available or simple Pulse
                      }}
                    >
                      <BrandLogoBadge size={80} radius={20} />
                    </div>
                  </div>

                  {/* Title */}
                  <h1
                    style={{
                      fontFamily: "'Orbitron', sans-serif",
                      fontSize: isMobile ? "1.8rem" : "2.5rem",
                      fontWeight: 900,
                      margin: "0 0 10px 0",
                      color: "#ff1026",
                      letterSpacing: "0.1em",
                      filter: "drop-shadow(0 0 10px rgba(255,16,38,0.5))",
                    }}
                  >
                    TO BE NUMBER ONE ESPORTS
                  </h1>

                  <p style={{ color: "#6b7280", marginBottom: "30px", fontSize: "1rem", letterSpacing: "0.05em" }}>
                    ADVANCED ANALYTICS SYSTEM
                  </p>

                  {/* Upload Button */}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                      background: "linear-gradient(135deg, #ff1026, #a30013)",
                      padding: "16px 32px",
                      borderRadius: "12px",
                      color: "#ffffff",
                      fontFamily: "'Orbitron', sans-serif",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      cursor: isProcessingUpload ? "not-allowed" : "pointer",
                      transition: "all 0.3s",
                      boxShadow: "0 0 20px rgba(255,16,38,0.4)",
                      border: "none",
                      position: "relative",
                      overflow: "hidden",
                      opacity: isProcessingUpload ? 0.6 : 1,
                      pointerEvents: isProcessingUpload ? "none" : "auto",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.02)";
                      e.currentTarget.style.boxShadow = "0 0 30px rgba(255,16,38,0.6)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.boxShadow = "0 0 20px rgba(255,16,38,0.4)";
                    }}
                  >
                    <Upload size={20} />
                    <span>{isProcessingUpload ? "PROCESSING FILE..." : "UPLOAD DATA FILE"}</span>
                    <input
                      type="file"
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      style={{ display: "none" }}
                      onChange={handleFile}
                    />
                  </label>

                  <div style={{ marginTop: "20px", fontSize: "0.85rem", color: "#7f1d1d" }}>
                    Support: .xlsx files (Scrim Logs)
                  </div>

                  {/* Decorative Elements */}
                  <div style={{
                    position: "absolute",
                    bottom: "20px",
                    left: "0",
                    right: "0",
                    display: "flex",
                    justifyContent: "center",
                    gap: "10px",
                    opacity: 0.3
                  }}>
                    {[...Array(3)].map((_, i) => (
                      <div key={i} style={{ width: "4px", height: "4px", background: "#ff1026", borderRadius: "50%" }} />
                    ))}
                  </div>

                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {(isProcessingUpload || uploaded) && <GateEffect processing={isProcessingUpload} />}
        </div>
      </>
    );
  }

  // ---------- Routes ----------
  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route
        path="/upload"
        element={isAuthenticated ? <UploadPage /> : <Navigate to="/" replace />}
      />

      <Route
        path="/dashboard"
        element={
          isAuthenticated ? (
            <HomePage
              games={homeGames}
              comboStats={comboStats}
              dropdownDates={dropdownDates}
              sourceName={excelFile?.name}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      <Route
        path="/draft"
        element={
          isAuthenticated ? (
            <DraftEntryGate>
              <DraftPage
                heroes={heroList}
                comboStats={comboStats}
                excelData={excelWb}
                excelFileBlob={excelFile}
                games={games}
                dropdownDates={dropdownDates}
              />
            </DraftEntryGate>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      <Route
        path="/win-rate"
        element={isAuthenticated ? <WinRateTierSelection games={games} /> : <Navigate to="/" replace />}
      />
      <Route
        path="/meta-lab"
        element={isAuthenticated ? <MetaLab /> : <Navigate to="/" replace />}
      />
      <Route
        path="/team-performance"
        element={
          isAuthenticated ? (
            <TeamPerformancePage teamPerformance={teamPerformance} comboStats={comboStats} excelFile={excelFile} games={games} />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/draft-recap"
        element={
          isAuthenticated ? (
            <DraftRecapPage games={games} excelFile={excelFile} />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/tactic-board"
        element={
          isAuthenticated ? (
            <TacticBoardPage games={games} excelFile={excelFile} />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/tier-data"
        element={
          isAuthenticated ? (
            <TierData
              games={games}
              comboStats={comboStats}
              dropdownDates={dropdownDates}
              excelFile={excelFile}
              heroImageOf={getHeroImageUrl}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="*" element={<Navigate to={isAuthenticated ? "/upload" : "/"} replace />} />
    </Routes>
  );
}
